<template>
  <!-- 库存查询（路桥定制：按商品 / 按批次，批次页签增加合同编号、堆料区、二维码） -->
  <div class="inventory-list">
    <list-page>
      <div class="tabs-header">
        <div class="tab-item" :class="activeTab === 0 ? 'active' : ''" @click="switchTab(0)">按商品查询</div>
        <div class="tab-item" :class="activeTab === 1 ? 'active' : ''" @click="switchTab(1)">按批次查询</div>
      </div>

      <search-condition @search="resetSearch" :show-search="true">
        <template #default>
          <product-select v-model="searchInfo.productId" placeholder="请选择商品" :multi="true">
            <template #prefix>商品名称</template>
          </product-select>
          <el-select v-model="searchInfo.warehouseId" placeholder="请选择仓库" clearable>
            <template #prefix>仓库</template>
            <el-option v-for="w in warehouseOptions" :key="w.id" :value="w.id" :label="w.warehouseName"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button v-if="activeTab !== 0" type="write" el-type="warning" @click="generateSelectedBatchQr">生成二维码</page-button>
          <print-button v-if="activeTab !== 0" template-key="LUQIAO_STOCK_BATCH" print-type="continuous"
                        :find-print-data="getBatchPrintData">打印二维码</print-button>
        </template>
      </search-condition>

      <!-- 按商品查询 -->
      <template v-if="activeTab === 0">
        <list-table>
          <yq-table :data="dataList" height="100%" stripe border page-name="inventoryProduct">
            <yq-table-column label="仓库名称" prop="warehouseName" min-width="180rem">
              <template v-slot="{row}">{{getWarehouseName(row.warehouseId)}}</template>
            </yq-table-column>
            <yq-table-column label="商品(SKU)名称" prop="productName" min-width="220rem" :show-overflow-tooltip="true"/>
            <!-- 路桥定制：商品名称后面显示规格型号 -->
            <yq-table-column label="规格型号" prop="productSpec" min-width="160rem" :show-overflow-tooltip="true"/>
            <yq-table-column label="基本单位" prop="unitNameBase" width="120rem" align="center"/>
            <yq-table-column label="数量" prop="qty" width="120rem" align="right">
              <template v-slot="{row}">
                <a v-if="row.serialEnabled === 1" @click="openSerial(row)">{{row.qty}}</a>
                <span v-else>{{row.qty}}</span>
              </template>
            </yq-table-column>
            <yq-table-column label="操作" prop="options" :align="'center'" fixed="right" width="180rem" :show-overflow-tooltip="false">
              <template v-slot="{row}">
                <table-button v-if="row.serialEnabled === 1" @click="openSerial(row)">序列号</table-button>
                <table-button @click="openLog(row)">变更记录</table-button>
                <table-button @click="openBatch(row)">批次</table-button>
              </template>
            </yq-table-column>
          </yq-table>
        </list-table>
      </template>

      <!-- 按批次查询（路桥定制） -->
      <template v-else>
        <list-table>
          <yq-table :data="dataList" height="100%" stripe border page-name="inventoryBatch"
                    @selectionChange="batchSelectionChange">
            <yq-table-column type="selection" prop="selection" width="60rem" align="center" fixed="left"/>
            <yq-table-column label="批次号" prop="batchNumber" width="180rem"/>
            <yq-table-column label="商品(SKU)" prop="productName" min-width="200rem" :show-overflow-tooltip="true"/>
            <!-- 路桥定制：商品名称后面显示规格型号 -->
            <yq-table-column label="规格型号" prop="productSpec" min-width="160rem" :show-overflow-tooltip="true"/>
            <yq-table-column label="仓库" prop="warehouseName" min-width="160rem">
              <template v-slot="{row}">{{getWarehouseName(row.warehouseId)}}</template>
            </yq-table-column>
            <!-- 路桥定制：合同编号存批次备用字段1，堆料区存备用字段2 -->
            <yq-table-column label="合同编号" prop="bankup1" min-width="200rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">{{row.bankup1 || '—'}}</template>
            </yq-table-column>
            <yq-table-column label="堆料区" prop="bankup2" min-width="160rem" :show-overflow-tooltip="true">
              <template v-slot="{row}">{{areaName(row.bankup2)}}</template>
            </yq-table-column>
            <yq-table-column label="基本单位" prop="unitNameBase" width="100rem" align="center"/>
            <yq-table-column label="数量" prop="qty" width="110rem" align="right"/>
            <!-- 路桥定制：批次二维码存备用字段3，人工生成 -->
            <yq-table-column label="二维码" prop="bankup3" width="130rem" align="center">
              <template v-slot="{row}">
                <image-show v-if="row.bankup3" v-model="row.bankup3" width="0.8rem" height="0.8rem"/>
                <el-button v-else link type="primary" @click="generateBatchQr(row)">生成二维码</el-button>
              </template>
            </yq-table-column>
          </yq-table>
        </list-table>
      </template>

      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 序列号弹窗 -->
    <el-dialog v-model="showSerial" :title="`序列号列表 · ${currentRow ? currentRow.productName : ''}`" dialog-width="3" :close-on-click-modal="false">
      <dialog-table>
        <div class="serial-tags">
          <span class="serial-tag" v-for="(s, i) in currentSerialList" :key="i">{{s.serialNumber || s}}</span>
        </div>
      </dialog-table>
      <template #footer>
        <page-button @click="showSerial = false">关闭</page-button>
      </template>
    </el-dialog>

    <!-- 变更记录弹窗 -->
    <el-dialog v-model="showLog" :title="`数量变更记录 · ${currentRow ? currentRow.productName : ''}`" dialog-width="3" :close-on-click-modal="false">
      <dialog-table>
        <yq-table :data="currentLogList || []" border stripe>
          <yq-table-column label="单据编号" prop="billNo"/>
          <yq-table-column label="单据类型" prop="billType" width="120rem" align="center">
            <template v-slot="{row}">
              <el-tag size="small" :type="billTypeTag(row.billType)">{{billTypeName(row.billType)}}</el-tag>
            </template>
          </yq-table-column>
          <yq-table-column label="变更数量" prop="changeQty" width="120rem" align="right">
            <template v-slot="{row}">
              <span :style="{color: Number(row.changeQty) > 0 ? 'var(--el-color-success)' : 'var(--el-color-danger)'}">{{Number(row.changeQty) > 0 ? '+' : ''}}{{row.changeQty}}</span>
            </template>
          </yq-table-column>
          <yq-table-column label="操作员" prop="operatorName" width="120rem"/>
          <yq-table-column label="时间" prop="createdTime" width="180rem" class-name="muted"/>
        </yq-table>
      </dialog-table>
    </el-dialog>

    <!-- 批次弹窗 -->
    <el-dialog v-model="showBatch" :title="`当前有效批次 · ${currentRow ? currentRow.productName : ''}`" dialog-width="2" :close-on-click-modal="false">
      <dialog-table>
        <yq-table :data="currentBatchList || []" border stripe>
          <yq-table-column label="批次号" prop="batchNumber" width="180rem"/>
          <yq-table-column label="商品数量" prop="qty" align="right"/>
          <yq-table-column label="库存均价" prop="priceAvg" align="right"/>
          <yq-table-column label="批次入库时间" prop="createdTime" class-name="muted"/>
        </yq-table>
      </dialog-table>
    </el-dialog>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import DialogTable from "@/components/h5/layout/DialogTable.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import PrintButton from "@/views/h5/common/print/template/print-button.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import stockApi from "@/api/normal/stock/stockApi";
import productSerialApi from "@/api/normal/product/productSerialApi";
import ProductSelect from "@/components/h5/product/ProductSelect.vue";
import billTypeUtil from "@/views/h5/common/stock/BillTypeUtil";
import commonDataApi from "@/api/normal/common/commonDataApi";
import warehouseAreaApi from "@/api/dz-luqiao/warehouseAreaApi";
import ImageShow from "@/components/h5/image/ImageShow.vue";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "InventoryList",
  components: {
    ImageShow,
    PrintButton, DialogTable, YqTableColumn, TableButton, ProductSelect,
    SearchCondition, FormPage, ListTable, ListPage
  },
  data() {
    return {
      activeTab: 0,
      searchInfo: {pageNumber: 1},
      dataList: [],
      warehouseOptions: [],
      // 路桥定制：批次二维码勾选 + 堆料区名称
      batchSelected: [],
      areaNameMap: {},
      showSerial: false,
      showLog: false,
      showBatch: false,
      currentRow: null,
      currentSerialList: [],
      currentLogList: [],
      currentBatchList: [],
    };
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.init();
    this.resetSearch();
  },
  methods: {
    getWarehouseName(id) {
      return this.warehouseOptions.find(item => item.id === id)?.warehouseName || ''
    },
    init() {
      commonDataApi.queryAllWarehouse().then(list => this.warehouseOptions = list);
      // 路桥定制：堆料区名称映射
      warehouseAreaApi.queryAll().then(list => {
        const map = {};
        (list || []).forEach(area => map[area.id] = area.areaName);
        this.areaNameMap = map;
      }).catch(() => {});
    },
    switchTab(i) {
      this.activeTab = i;
      this.batchSelected = [];
      this.resetSearch();
    },
    async queryList() {
      const api = this.activeTab === 0 ? stockApi.queryList : stockApi.queryBatchList;
      const {records, totalCount} = await api(this.searchInfo);
      this.dataList = records;
      this.searchInfo.totalCount = totalCount;
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    // ==================== 路桥定制：批次二维码 ====================
    batchSelectionChange(list) {
      this.batchSelected = list || [];
    },
    areaName(areaId) {
      if (!areaId) return '—';
      return this.areaNameMap[areaId] || areaId;
    },
    /** 单行生成二维码 */
    async generateBatchQr(row) {
      const list = await stockApi.updateBatchQrCode([row.id]);
      this.applyBatchQr(list);
    },
    /** 勾选的多行批量生成二维码 */
    async generateSelectedBatchQr() {
      if (!this.batchSelected.length) {
        this.$message.error("请先勾选批次");
        return;
      }
      const list = await stockApi.updateBatchQrCode(this.batchSelected.map(row => row.id));
      this.applyBatchQr(list);
    },
    /**
     * 把后台返回的二维码直接回填到列表行，并给出提示
     *
     * 不重新查列表：生成完当前行立刻显示二维码，和堆料区页签的效果一致。
     * 后台一个都没生成成功时会抛错（比如二维码地址超出字段长度），由拦截器提示原因。
     */
    applyBatchQr(list) {
      const map = {};
      (list || []).forEach(item => {
        if (item && item.bankup3) {
          map[item.id] = item.bankup3;
        }
      });
      let count = 0;
      this.dataList.forEach(row => {
        if (map[row.id]) {
          row.bankup3 = map[row.id];
          count++;
        }
      });
      if (count > 0) {
        this.$message.success(`二维码已更新（${count} 个）`);
      } else {
        this.$message.warning("没有可更新的二维码");
      }
    },
    /**
     * 打印数据（连续打印）：勾选批次的二维码
     * 打印模板里用「二维码(自定义)」绑定 qrcodeImage
     */
    getBatchPrintData() {
      if (!this.batchSelected.length) {
        return null;
      }
      const rows = this.batchSelected.filter(row => row.bankup3);
      if (rows.length === 0) {
        this.$message.error("勾选的批次还没有二维码，请先生成二维码");
        return null;
      }
      return rows.map(row => ({
        productName: row.productName,
        warehouseName: this.getWarehouseName(row.warehouseId),
        batchNumber: row.batchNumber,
        contractNumber: row.bankup1 || "",
        qrcodeImage: row.bankup3,
      }));
    },

    // ==================== 商品页签原有功能 ====================
    async openSerial(row) {
      this.currentRow = row;
      const list = await productSerialApi.queryByProduct(row.productId);
      this.currentSerialList = list || [];
      this.showSerial = true;
    },
    async openLog(row) {
      this.currentRow = row;
      const list = await stockApi.queryLog({productId: row.productId, skuId: row.skuId, warehouseId: row.warehouseId});
      this.currentLogList = list || [];
      this.showLog = true;
    },
    async openBatch(row) {
      this.currentRow = row;
      const list = await stockApi.queryBatchByProduct(row.warehouseId, row.productId, row.skuId);
      this.currentBatchList = list || [];
      this.showBatch = true;
    },
    billTypeTag(type) {
      return billTypeUtil.getBillTypeTag(type);
    },
    billTypeName(type) {
      return billTypeUtil.getBillTypeName(type);
    }
  }
};
</script>

<style lang="scss" scoped>
.inventory-list {
  height: 100%;

  .tabs-header {
    display: flex;
    gap: 0;
    border-bottom: 1px solid var(--yq-border-color);
    background: #fff;
    border-radius: 8px 8px 0 0;

    .tab-item {
      padding: 12px 22px;
      font-size: 14px;
      color: var(--yq-font-color-tips);
      cursor: pointer;
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;

      &:hover {
        color: var(--el-color-primary);
      }

      &.active {
        color: var(--el-color-primary);
        border-bottom-color: var(--el-color-primary);
        font-weight: 600;
      }
    }
  }

  .batch-toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 10px;
    background: #fff;

    .batch-tips {
      margin-right: auto;
      font-size: 12px;
      color: var(--yq-font-color-desc);
    }
  }

  .qr-image {
    width: 60px;
    height: 60px;
  }

  .serial-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .serial-tag {
      background: var(--el-color-primary-light-9);
      color: var(--el-color-primary);
      padding: 3px 10px;
      border-radius: 4px;
      font-size: 13px;
    }
  }

  .muted {
    color: var(--yq-font-color-desc);
    font-size: 12px;
  }
}
</style>
